import React from 'react';
import {
  Box,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';

export type CommonTableColumn<T> = {
  key: string;
  header: React.ReactNode;
  align?: 'left' | 'right' | 'center';
  width?: number | string;
  minWidth?: number | string;
  render: (row: T) => React.ReactNode;
};

export type CommonTablePagination = {
  page: number; // 0-based
  rowsPerPage: number;
  total: number;
  rowsPerPageOptions?: number[];
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
};

export interface CommonTableProps<T> {
  columns: Array<CommonTableColumn<T>>;
  rows: T[];
  getRowId: (row: T) => string;
  isLoading?: boolean;
  error?: string | null;
  emptyLabel?: string;
  pagination?: CommonTablePagination;
  containerSx?: SxProps<Theme>;
  headerSx?: SxProps<Theme>;
}

export function CommonTable<T>({
  columns,
  rows,
  getRowId,
  isLoading = false,
  error = null,
  emptyLabel = 'No records found.',
  pagination,
  containerSx,
  headerSx,
}: CommonTableProps<T>) {
  const showEmpty = !isLoading && !error && rows.length === 0;

  return (
    <Paper
      variant="outlined"
      sx={[
        {
          height: '100%',
          borderRadius: 2,
          overflow: 'hidden',
          backgroundColor: 'transparent!important',
        },
        containerSx,
      ] as SxProps<Theme>}
    >
      <TableContainer sx={{ maxHeight: 'calc(100dvh - 250px)', height: '100%' }}>
        <Table stickyHeader size="small" aria-label="table">
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell
                  key={col.key}
                  align={col.align ?? 'left'}
                  sx={[
                    {
                      fontWeight: 700,
                      bgcolor: 'background.default',
                      borderBottomColor: 'divider',
                      ...(col.width !== undefined ? { width: col.width } : null),
                      ...(col.minWidth !== undefined ? { minWidth: col.minWidth } : null),
                    },
                    headerSx,
                  ] as SxProps<Theme>}
                >
                  {col.header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {isLoading
              ? Array.from({ length: Math.min(8, pagination?.rowsPerPage ?? 8) }).map((_, i) => (
                  <TableRow key={`loading-${i}`}>
                    {columns.map((c) => (
                      <TableCell key={`${c.key}-${i}`}>
                        <Skeleton variant="text" height={22} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : rows.map((row) => (
                  <TableRow key={getRowId(row)} hover sx={{ '& td': { borderBottomColor: 'divider' } }}>
                    {columns.map((col) => (
                      <TableCell key={col.key} align={col.align ?? 'left'} sx={{ verticalAlign: 'middle' }}>
                        {col.render(row)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}

            {error ? (
              <TableRow>
                <TableCell colSpan={columns.length} sx={{ py: 3 }}>
                  <Typography variant="body2" color="error">
                    {error}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : null}

            {showEmpty ? (
              <TableRow>
                <TableCell colSpan={columns.length} sx={{ py: 3 }}>
                  <Box sx={{ display: 'grid', placeItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      {emptyLabel}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </TableContainer>

      {pagination ? (
        <TablePagination
          component="div"
          count={pagination.total}
          page={pagination.page}
          onPageChange={(_, nextPage) => pagination.onPageChange(nextPage)}
          rowsPerPage={pagination.rowsPerPage}
          onRowsPerPageChange={(e) => pagination.onRowsPerPageChange(parseInt(e.target.value, 10))}
          rowsPerPageOptions={pagination.rowsPerPageOptions ?? [10, 25, 50]}
          sx={{
            borderTop: (theme) => `1px solid ${theme.palette.divider}`,
          }}
        />
      ) : null}
    </Paper>
  );
}

